import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAdmin } from "../../App";
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  LogOut,
  ShieldCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  MapPin,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const formatPrice = (price) => {
  if (price >= 10000000) {
    return `₹${(price / 10000000).toFixed(2)} Cr`;
  } else if (price >= 100000) {
    return `₹${(price / 100000).toFixed(2)} L`;
  }
  return `₹${price.toLocaleString("en-IN")}`;
};

const AdminProperties = () => {
  const { admin, adminLogout, adminToken } = useAdmin();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("all");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, property: null });
  const limit = 10;

  useEffect(() => {
    fetchProperties();
  }, [adminToken, page, city, category]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (city && city !== "all") params.append("city", city);
      if (category && category !== "all") params.append("category", category);
      params.append("limit", limit);
      params.append("skip", page * limit);

      const response = await axios.get(`${API}/admin/properties?${params.toString()}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setProperties(response.data.properties);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to fetch properties");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchProperties();
  };

  const handleDelete = async () => {
    if (!deleteDialog.property) return;

    try {
      await axios.delete(`${API}/admin/properties/${deleteDialog.property.property_id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Property deleted successfully");
      setDeleteDialog({ open: false, property: null });
      fetchProperties();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate("/admin");
  };

  const sidebarItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Building2, label: "Properties", path: "/admin/properties", active: true },
    { icon: MessageSquare, label: "Inquiries", path: "/admin/inquiries" },
  ];

  const cities = ["Bengaluru", "Mumbai", "Hyderabad", "Chennai", "Pune", "Delhi-NCR"];
  const categories = [
    { value: "new_launch", label: "New Launch" },
    { value: "under_construction", label: "Under Construction" },
    { value: "ready_to_move", label: "Ready to Move" },
    { value: "upcoming", label: "Upcoming" },
  ];

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-stone-900 flex" data-testid="admin-properties">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-800 border-r border-stone-700 flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-lg text-white">RERA Admin</div>
              <div className="text-xs text-stone-400">Control Panel</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    item.active
                      ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      : "text-stone-400 hover:bg-stone-700 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
              {admin?.name?.charAt(0) || "A"}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{admin?.name}</p>
              <p className="text-stone-400 text-xs">{admin?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-stone-800 border-b border-stone-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl text-white">Properties</h1>
              <p className="text-stone-400 text-sm">{total} properties found</p>
            </div>
            <Link to="/admin/properties/new">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add Property
              </Button>
            </Link>
          </div>
        </header>

        <div className="p-8">
          {/* Filters */}
          <div className="bg-stone-800 border border-stone-700 p-4 rounded-sm mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" strokeWidth={1.5} />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search properties..."
                  className="pl-10 bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                />
              </div>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Search
              </Button>
            </div>
          </div>

          {/* Properties Table */}
          <div className="bg-stone-800 border border-stone-700 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-700">
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-stone-400 font-medium">Property</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-stone-400 font-medium">Location</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-stone-400 font-medium">Price</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-stone-400 font-medium">Category</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-stone-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-stone-700">
                      <td className="px-6 py-4" colSpan={5}>
                        <div className="h-12 bg-stone-700 animate-pulse rounded" />
                      </td>
                    </tr>
                  ))
                ) : properties.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-stone-400">
                      No properties found
                    </td>
                  </tr>
                ) : (
                  properties.map((property) => (
                    <tr key={property.property_id} className="border-b border-stone-700 hover:bg-stone-700/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={property.images?.[0] || "https://via.placeholder.com/60"}
                            alt={property.name}
                            className="w-12 h-12 object-cover rounded-sm"
                          />
                          <div>
                            <p className="text-white font-medium">{property.name}</p>
                            <p className="text-stone-400 text-xs">{property.builder}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-stone-300">
                          <MapPin className="w-4 h-4" strokeWidth={1.5} />
                          {property.locality}, {property.city}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white font-mono">{formatPrice(property.price_min)}</p>
                        <p className="text-stone-400 text-xs">onwards</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-stone-700 text-stone-300 text-xs uppercase rounded">
                          {property.category?.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <a
                            href={`/property/${property.property_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-stone-600 rounded-sm transition-colors text-stone-400 hover:text-white"
                            title="View"
                          >
                            <Eye className="w-4 h-4" strokeWidth={1.5} />
                          </a>
                          <Link
                            to={`/admin/properties/${property.property_id}/edit`}
                            className="p-2 hover:bg-stone-600 rounded-sm transition-colors text-stone-400 hover:text-amber-500"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" strokeWidth={1.5} />
                          </Link>
                          <button
                            onClick={() => setDeleteDialog({ open: true, property })}
                            className="p-2 hover:bg-stone-600 rounded-sm transition-colors text-stone-400 hover:text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-stone-700">
                <p className="text-stone-400 text-sm">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="border-stone-600 text-stone-400 hover:bg-stone-700"
                  >
                    <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                  <span className="text-stone-400 text-sm">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="border-stone-600 text-stone-400 hover:bg-stone-700"
                  >
                    <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-stone-800 border-stone-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Property</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Are you sure you want to delete "{deleteDialog.property?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-stone-700 text-white hover:bg-stone-600 border-stone-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminProperties;
